import { CHARACTER_REGION_LABELS, type CharacterGearRow, type CharacterView } from '@thottbot/shared';
import { Badge } from '../../components/Badge';
import { PaperDoll } from './PaperDoll';

function statLine(row: CharacterGearRow): string[] {
  const lines: string[] = [];
  const item = row.item;
  if (!item) return lines;
  if (item.itemLevel !== undefined) lines.push(`Item level ${item.itemLevel}`);
  if (item.armor) lines.push(`${item.armor} Armor`);
  if (item.damage)
    lines.push(
      `${item.damage.min} - ${item.damage.max} damage, speed ${item.damage.speed.toFixed(2)}`,
    );
  for (const s of item.stats ?? []) lines.push(`${s.value > 0 ? '+' : ''}${s.value} ${s.name}`);
  return lines;
}

/** The in-game character window for an imported character: what it wears, its plate, attributes. */
export function CharacterSheet({ c }: { c: CharacterView }) {
  const a = c.attributes;
  const partial = a.itemsCounted < a.itemsEquipped;
  const badged = c.gear.find((g) => g.item?.dataStatus)?.item;
  return (
    <PaperDoll
      testId="character-sheet"
      title={c.name}
      guild={c.guild}
      classIconUrl={c.classIconUrl}
      classLabel={c.class}
      plate={
        <>
          <div className="sheet-level">
            Level {c.level} {c.race} {c.class}
          </div>
          <div className="muted">
            {c.realmName} ({CHARACTER_REGION_LABELS[c.region]}) · {c.faction}
          </div>
        </>
      }
      slots={c.gear.map((row) => ({
        slot: row.slot,
        label: row.label,
        item: row.item,
        lines: statLine(row),
        contextLines: row.enchant ? [`Enchant: ${row.enchant.text}`] : [],
        artUrl: row.slotArtUrl,
      }))}
      attributes={a}
      attributesNote={
        a.itemsEquipped === 0
          ? 'No gear to sum.'
          : partial
            ? `From ${a.itemsCounted} of ${a.itemsEquipped} equipped items the database knows; base stats not included.`
            : 'Gear only; base stats not included.'
      }
      status={badged?.dataStatus && <Badge status={badged.dataStatus} build={badged.sourceBuild} />}
    />
  );
}
